import "../chunk-D57E6H3M.mjs";

// src/hooks/useSlideToFit.ts
import { useCallback, useLayoutEffect, useRef } from "react";
var SLIDE_MS = 280;
var SLIDE_CURVE = [0.23, 1, 0.32, 1];
var SLIDE_EASE = `cubic-bezier(${SLIDE_CURVE.join(", ")})`;
var REVEALING_CHAR = ".buyscout-char-fade-in";
function release(element, inner) {
  element.style.removeProperty("box-sizing");
  element.style.removeProperty("width");
  element.style.removeProperty("height");
  element.style.removeProperty("overflow");
  element.style.removeProperty("transition");
  inner.style.removeProperty("width");
  inner.style.removeProperty("max-width");
  inner.style.removeProperty("flex");
}
function bezier(u, p1, p2) {
  return 3 * (1 - u) ** 2 * u * p1 + 3 * (1 - u) * u ** 2 * p2 + u ** 3;
}
function slideTimeAt(progress) {
  const [x1, y1, x2, y2] = SLIDE_CURVE;
  let low = 0;
  let high = 1;
  for (let i = 0; i < 20; i++) {
    const mid = (low + high) / 2;
    if (bezier(mid, y1, y2) < progress) low = mid;
    else high = mid;
  }
  return bezier(high, x1, x2);
}
function passedAt(a0, a1, edge0, edge1) {
  if (a0 <= edge0) return 0;
  const closing = edge1 - edge0 - (a1 - a0);
  return closing > 0 ? Math.min(1, (a0 - edge0) / closing) : 1;
}
function centreOf(element) {
  const { left, right, top, bottom } = element.getBoundingClientRect();
  return { x: (left + right) / 2, y: (top + bottom) / 2 };
}
function sizeOf(element) {
  const { width, height } = element.getBoundingClientRect();
  return { width, height };
}
function useSlideToFit(ref, content, active) {
  const settledRef = useRef(null);
  const timerRef = useRef(void 0);
  const wasActiveRef = useRef(active);
  const activeRef = useRef(active);
  const fit = useCallback((ending) => {
    const element = ref.current;
    const inner = element?.firstElementChild;
    if (!element || !(inner instanceof HTMLElement)) return;
    const sliding = element.style.width !== "";
    const from = sliding ? sizeOf(element) : settledRef.current;
    release(element, inner);
    const to = sizeOf(element);
    settledRef.current = to;
    const unchanged = !from || Math.abs(from.width - to.width) < 0.5 && Math.abs(from.height - to.height) < 0.5;
    if (!(activeRef.current || sliding || ending) || unchanged || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      clearTimeout(timerRef.current);
      return;
    }
    const revealing = Array.from(inner.querySelectorAll(REVEALING_CHAR));
    const endBox = element.getBoundingClientRect();
    const endCentres = revealing.map(centreOf);
    inner.style.width = `${inner.getBoundingClientRect().width}px`;
    inner.style.maxWidth = "none";
    inner.style.flex = "none";
    element.style.boxSizing = "border-box";
    element.style.overflow = "hidden";
    element.style.width = `${from.width}px`;
    element.style.height = `${from.height}px`;
    const startBox = element.getBoundingClientRect();
    revealing.forEach((char, index) => {
      const start = centreOf(char);
      const end = endCentres[index];
      const progress = Math.max(
        passedAt(start.x, end.x, startBox.right, endBox.right),
        passedAt(start.y, end.y, startBox.bottom, endBox.bottom)
      );
      if (progress === 0) return;
      const uncoveredAt = SLIDE_MS * slideTimeAt(progress);
      for (const animation of char.getAnimations()) {
        const current = Number(animation.currentTime ?? 0);
        if (current > -uncoveredAt) animation.currentTime = -uncoveredAt;
      }
    });
    element.style.transition = `width ${SLIDE_MS}ms ${SLIDE_EASE}, height ${SLIDE_MS}ms ${SLIDE_EASE}`;
    element.style.width = `${to.width}px`;
    element.style.height = `${to.height}px`;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(release, SLIDE_MS, element, inner);
  }, [ref]);
  useLayoutEffect(() => {
    const ending = wasActiveRef.current && !active;
    wasActiveRef.current = active;
    activeRef.current = active;
    fit(ending);
  }, [fit, content, active]);
  const attachedRef = useRef(null);
  useLayoutEffect(() => {
    const element = ref.current;
    if (attachedRef.current?.element === element) return;
    attachedRef.current?.detach();
    attachedRef.current = element ? follow(element, fit, activeRef, settledRef) : null;
  });
  useLayoutEffect(() => () => {
    attachedRef.current?.detach();
    clearTimeout(timerRef.current);
  }, []);
}
function follow(element, fit, activeRef, settledRef) {
  const observer = new ResizeObserver(() => {
    if (element.style.width !== "") return;
    if (activeRef.current) fit(false);
    else settledRef.current = sizeOf(element);
  });
  observer.observe(element);
  const onLoad = (event) => {
    if (event.target instanceof HTMLImageElement) fit(false);
  };
  element.addEventListener("load", onLoad, true);
  return {
    element,
    detach: () => {
      observer.disconnect();
      element.removeEventListener("load", onLoad, true);
    }
  };
}
export {
  useSlideToFit
};
//# sourceMappingURL=useSlideToFit.mjs.map